import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Checkbox, Chip, IconButton, Stack, Typography } from '@mui/material';

import Image from '~/components/Common/Image';

import type { MediaImage } from '../types';
import { imageSelectCheckboxSx } from '../utils';

interface Props {
  canToggleHidden: boolean;
  image: MediaImage;
  isHidden: boolean;
  isSelected: boolean;
  slotLabel: string;
  onDeleteImage: (image: MediaImage) => void;
  onEditImage: (image: MediaImage) => void;
  onToggleHiddenImage: (publicId: string) => Promise<void>;
  onToggleSelected: (publicId: string) => void;
}

export default function OfferGalleryTile({
  canToggleHidden,
  image,
  isHidden,
  isSelected,
  slotLabel,
  onDeleteImage,
  onEditImage,
  onToggleHiddenImage,
  onToggleSelected,
}: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: image.publicId });
  const label = image.title || 'Offer image';

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: '16 / 9',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.72 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : 'auto',
        '&:hover .OfferBuilderOfferGallery-overlay': {
          opacity: 1,
          visibility: 'visible',
          pointerEvents: 'auto',
        },
      }}
    >
      <Image
        publicId={image.publicId}
        alt={label}
        loading="lazy"
        options={{ width: 420, height: 236 }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          filter: isHidden ? 'grayscale(1) opacity(0.42)' : undefined,
        }}
      />
      <Checkbox
        checked={isSelected}
        aria-label={`Select ${image.title || image.publicId}`}
        sx={imageSelectCheckboxSx}
        onPointerDown={(event) => event.stopPropagation()}
        onChange={() => onToggleSelected(image.publicId)}
      />
      <Stack spacing={0.75} sx={getImageLabelsStackSx(isHidden)}>
        <Chip label={slotLabel} size="small" sx={imageSourceChipSx} />
      </Stack>
      <Stack
        className="OfferBuilderOfferGallery-overlay"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          left: 0,
          opacity: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          transition: 'opacity 160ms ease-in-out, visibility 160ms ease-in-out',
          px: 1.5,
          py: 1.25,
          background: 'linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.62))',
        }}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <Typography
          variant="body1"
          color="common.white"
          fontWeight={500}
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            visibility: image.title ? 'visible' : 'hidden',
          }}
        >
          {image.title}
        </Typography>
        <Stack direction="row" spacing={0.75}>
          <IconButton aria-label="Edit image" sx={imageActionButtonSx} onClick={() => onEditImage(image)}>
            <EditIcon fontSize="small" />
          </IconButton>
          {canToggleHidden && (
            <IconButton
              aria-label={isHidden ? 'Show image' : 'Hide image'}
              sx={imageActionButtonSx}
              onClick={() => void onToggleHiddenImage(image.publicId)}
            >
              <VisibilityOffIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton aria-label="Delete image" sx={imageActionButtonSx} onClick={() => onDeleteImage(image)}>
            <DeleteIcon color="error" fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
}

const imageActionButtonSx = {
  width: 34,
  height: 34,
  bgcolor: 'common.white',
  color: 'grey.800',
  '&:hover': {
    bgcolor: 'grey.100',
  },
};

function getImageLabelsStackSx(isHidden: boolean) {
  return {
    ...imageLabelsStackSx,
    maxWidth: isHidden ? 'min(220px, calc(100% - 104px))' : imageLabelsStackSx.maxWidth,
  };
}

const imageLabelsStackSx = {
  position: 'absolute',
  top: 10,
  left: 10,
  maxWidth: 'min(220px, calc(100% - 20px))',
  alignItems: 'flex-start',
};

const imageSourceChipSx = {
  maxWidth: '100%',
  bgcolor: 'grey.800',
  color: 'common.white',
  fontWeight: 700,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
